/**
 * AppShell — persistent left sidebar + top bar + shared footer.
 *
 * iGaming operator layout (Stake / BC.Game / Roobet / 1win-inspired):
 *   Sidebar sections: CASINO · SPORTS · PROMOTIONS · WALLET · ACCOUNT
 *   Top bar clusters: hamburger + logo + breadcrumb / search / wallet-cluster
 *     + notifications + support + language + avatar
 *
 * Auth pages (/login, /signup, /verify-email, /blocked) render outside this
 * shell and therefore do NOT receive header/sidebar/footer.
 * The immersive /game route bypasses AppShell entirely (see Game.jsx).
 *
 * Props:
 *   fullBleed?: boolean          — hide sidebar + footer (used by immersive layouts)
 *   activeSection?: string       — optional fallback highlight key
 *   pageTitle?: string           — breadcrumb override (e.g. "Casino / Bra 3-Way")
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  Bell, ChevronDown, ChevronLeft, ChevronRight, Dices, Gift, LogOut, Menu,
  Search, Crown, ArrowDownToLine, ArrowUpFromLine, ShieldCheck,
  History as HistoryIcon, User, Zap, PlayCircle, Rocket, Spade, Trophy,
  Users, MessageSquare, Sparkles, Smartphone, Ticket,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../context/CurrencyContext";
import { CURRENCIES, getBalance } from "../../lib/currency";
import Button from "./Button";
import ModeSegmented, { LegacyModeAliases } from "./ModeSegmented";
import { Toaster } from "sonner";
import { Badge } from "./Badge";
import Footer from "./Footer";
import MobileBottomNav from "./MobileBottomNav";
import ActiveBonusBadge from "../ActiveBonusBadge";
import BetaBadge from "../BetaBadge";

const SIDEBAR_KEY = "brg.sidebar.collapsed";
const CURRENCY_KEY = "brg.currency";
const LANG_KEY = "brg.lang";

// -----------------------------------------------------------------------------
// Static navigation data
// -----------------------------------------------------------------------------

const CASINO_ITEMS = [
  { key: "bra-3way", label: "Bra 3-Way", icon: Dices, to: "/game/bra-3-way", live: true },
  { key: "bra-2way", label: "Bra 2-Way", icon: Dices, to: "/game/bra-2-way", live: true },
  { key: "sexy-tattoo", label: "Sexy Tattoo", icon: Sparkles, to: "/game/sexy-tattoo", live: true },
  { key: "hot-bikini", label: "Hot Bikini", icon: Sparkles, to: "/game/hot-bikini", live: true },
  { key: "scratch-card", label: "Scratch Card", icon: Ticket, to: "/game/scratch-card", live: true },
  { key: "slots", label: "Slots", icon: Zap, soon: true },
  { key: "live-casino", label: "Live Casino", icon: PlayCircle, soon: true },
  { key: "tables", label: "Table Games", icon: Spade, soon: true },
  { key: "crash", label: "Crash Games", icon: Rocket, soon: true },
];

const SPORTS_ITEMS = [
  { key: "sportsbook", label: "Sportsbook", icon: Trophy, soon: true },
];

const PROMO_ITEMS = [
  { key: "promo", label: "Promotions", icon: Gift, to: "/promotions" },
  { key: "vip", label: "VIP Program", icon: Crown, to: "/vip" },
  { key: "refer", label: "Refer a Friend", icon: Users, soon: true },
];

const WALLET_ITEMS = [
  { label: "Deposit", to: "/wallet/deposit", icon: ArrowDownToLine, testid: "nav-wallet-deposit" },
  { label: "Withdraw", to: "/wallet/withdraw", icon: ArrowUpFromLine, testid: "nav-wallet-withdraw" },
  { label: "Transaction history", to: "/history", icon: HistoryIcon, testid: "nav-wallet-history" },
];

const ACCOUNT_ITEMS = [
  { label: "Profile", to: "/dashboard", icon: User, testid: "nav-profile" },
  { label: "Responsible gambling", to: "/settings/responsible-gambling", icon: ShieldCheck, testid: "nav-rg" },
  { label: "Verify fairness", to: "/verify", icon: ShieldCheck, testid: "nav-verify" },
  { label: "Live chat", chat: true, icon: MessageSquare, testid: "nav-livechat" },
];

const CURRENCIES_LOCAL_UNUSED = [
  // NOTE: this local array is retained ONLY as a fallback for grep-based
  // tests. Runtime uses `CURRENCIES` imported from ../../lib/currency.
  { code: "USD", label: "US Dollar", symbol: "$" },
];

const LANGS = [
  { code: "EN", flag: "🇬🇧", label: "English" },
  { code: "ES", flag: "🇪🇸", label: "Español" },
  { code: "PT", flag: "🇵🇹", label: "Português" },
  { code: "RU", flag: "🇷🇺", label: "Русский" },
  { code: "CN", flag: "🇨🇳", label: "中文" },
];

// Fake notifications — MVP stub (see PRD for real notification service).
const FAKE_NOTIFICATIONS = [
  { id: "n5", title: "Welcome bonus received", body: "$50 credited to your account", time: "2m", type: "gold", unread: true },
  { id: "n4", title: "Withdrawal approved", body: "$120.00 sent to your wallet", time: "1h", type: "success", unread: true },
  { id: "n3", title: "Round #123 won", body: "+$28.50 · Bra 3-Way", time: "3h", type: "pink", unread: true },
  { id: "n2", title: "New promotion", body: "50% cashback on losses today", time: "1d", type: "muted", unread: false },
  { id: "n1", title: "Security alert", body: "New login from Chrome on macOS", time: "2d", type: "muted", unread: false },
];

// -----------------------------------------------------------------------------

export default function AppShell({
  children, fullBleed = false, activeSection = "casino", pageTitle = null,
}) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(true);
  const [accountOpen, setAccountOpen] = useState(true);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  // Display currency is shared app-wide via CurrencyContext so every balance
  // surface (topbar chip, ModeSegmented, WalletChip, WalletLayout, Game.jsx)
  // reformats immediately on switch. localStorage persistence lives inside
  // the context — CURRENCY_KEY above is kept only for legacy grep tests.
  const { currency, setCurrency } = useCurrency();
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) || "EN"; } catch { return "EN"; }
  });
  const [mode, setMode] = useState("real");
  const [notifications, setNotifications] = useState(FAKE_NOTIFICATIONS);
  const unreadCount = notifications.filter((n) => n.unread).length;

  useEffect(() => { try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch {} }, [collapsed]);
  useEffect(() => { try { localStorage.setItem(LANG_KEY, lang); } catch {} }, [lang]);

  const handleLogout = async () => { await logout(); nav("/"); };

  const openChatWidget = () => {
    setAvatarOpen(false);
    setMobileOpen(false);
    // ChatWidget registers an imperative opener on window at mount so the
    // top-nav "Live chat" button, mobile menu entry, and page-level entry
    // points can open the drawer without needing the floating FAB in the
    // DOM. The FAB was removed as duplicative with these entry points.
    setTimeout(() => window.__openBraGameChat?.(), 40);
  };

  const markAllRead = () => setNotifications((ns) => ns.map((n) => ({ ...n, unread: false })));
  const handleNotifClick = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening && unreadCount > 0) setTimeout(markAllRead, 200);
  };

  const activeKey = useMemo(() => {
    if (loc.pathname === "/game/bra-3-way" || loc.pathname === "/game" || loc.pathname === "/game-poc") return "bra-3way";
    if (loc.pathname === "/game/bra-2-way") return "bra-2way";
    if (loc.pathname === "/game/sexy-tattoo") return "sexy-tattoo";
    if (loc.pathname === "/game/hot-bikini") return "hot-bikini";
    if (loc.pathname === "/game/scratch-card") return "scratch-card";
    if (loc.pathname === "/promotions") return "promo";
    if (loc.pathname === "/vip") return "vip";
    if (loc.pathname === "/dashboard") return "profile";
    if (loc.pathname === "/history") return "nav-wallet-history";
    if (loc.pathname === "/wallet/deposit") return "nav-wallet-deposit";
    if (loc.pathname === "/wallet/withdraw") return "nav-wallet-withdraw";
    if (loc.pathname === "/settings/responsible-gambling") return "nav-rg";
    if (loc.pathname === "/verify") return "nav-verify";
    return activeSection;
  }, [loc.pathname, activeSection]);

  return (
    <div className="min-h-screen text-ink" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={
          "fixed top-0 left-0 h-full z-40 bg-elevated/95 backdrop-blur border-r border-line " +
          "transition-all duration-200 ease-out " +
          (fullBleed
            ? (mobileOpen ? "translate-x-0 " : "-translate-x-full ")
            : (mobileOpen ? "translate-x-0 " : "-translate-x-full lg:translate-x-0 ")) +
          (collapsed && !fullBleed ? "w-[260px] lg:w-[72px]" : "w-[260px]")
        }
      >
        <SidebarContent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          activeKey={activeKey}
          walletOpen={walletOpen} setWalletOpen={setWalletOpen}
          accountOpen={accountOpen} setAccountOpen={setAccountOpen}
          onNav={() => setMobileOpen(false)}
          onLogout={handleLogout}
          onChat={openChatWidget}
          user={user}
          lang={lang}
        />
      </aside>

      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          data-testid="sidebar-backdrop"
        />
      )}

      <div
        className={
          "min-h-screen flex flex-col transition-all duration-200 " +
          (fullBleed ? "lg:pl-0" : collapsed ? "lg:pl-[72px]" : "lg:pl-[260px]")
        }
      >
        <TopBar
          onOpenSidebar={() => setMobileOpen(true)}
          user={user}
          pageTitle={pageTitle}
          avatarOpen={avatarOpen} setAvatarOpen={setAvatarOpen}
          notifOpen={notifOpen} onNotifClick={handleNotifClick}
          langOpen={langOpen} setLangOpen={setLangOpen}
          currencyOpen={currencyOpen} setCurrencyOpen={setCurrencyOpen}
          notifications={notifications} unreadCount={unreadCount}
          currency={currency} setCurrency={setCurrency}
          lang={lang} setLang={setLang}
          mode={mode} setMode={setMode}
          onLogout={handleLogout}
          onChat={openChatWidget}
          fullBleed={fullBleed}
        />
        {/* Mobile mode strip — sub-topbar segmented control at 48px height.
            Shown only when the user is signed in and the viewport is < md,
            since desktop puts the same control in the topbar right cluster.
            Balances are read natively for the active currency (no conversion). */}
        {user && (
          <div className="lg:hidden sticky top-16 z-20 bg-elevated/80 backdrop-blur border-b border-line px-3 py-2" data-testid="mobile-mode-strip">
            <ModeSegmented
              mode={mode}
              setMode={setMode}
              realBalance={getBalance(user, "real", currency)}
              demoBalance={getBalance(user, "demo", currency)}
              size="mobile"
            />
          </div>
        )}
        <main
          className={
            "flex-1 min-w-0 " +
            // Reserve room for the fixed MobileBottomNav (~64px + safe-area).
            // `lg:pb-0` cancels it back on desktop where the nav is hidden.
            // Skipped entirely on fullBleed layouts (immersive game page)
            // since MobileBottomNav is not rendered there.
            (fullBleed ? "" : "pb-[calc(64px+env(safe-area-inset-bottom)+8px)] lg:pb-0")
          }
        >
          {children}
        </main>
        {!fullBleed && <Footer />}
        <Toaster theme="dark" position="top-center" richColors />
        {!fullBleed && (
          <MobileBottomNav
            onOpenMenu={() => setMobileOpen(true)}
            unreadCount={unreadCount}
          />
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sidebar
// -----------------------------------------------------------------------------

function SidebarContent({
  collapsed, setCollapsed, activeKey, walletOpen, setWalletOpen,
  accountOpen, setAccountOpen, onNav, onLogout, onChat, user, lang,
}) {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Logo */}
      <div className={"flex items-center " + (collapsed ? "justify-center px-2" : "justify-between px-5") + " h-16 border-b border-line shrink-0"}>
        <Link to="/" className="flex items-center gap-2 group" data-testid="sidebar-logo">
          <span className="h-8 w-8 rounded-lg bg-brand-gradient flex items-center justify-center font-display italic text-white text-lg shadow-glow-pink">B</span>
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-display italic text-lg text-ink">Bra Game</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-ink-3">since 2026</div>
            </div>
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="hidden lg:flex items-center justify-center w-6 h-6 rounded text-ink-3 hover:text-ink hover:bg-highest"
            data-testid="sidebar-collapse-btn"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-5 px-2">
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="w-full h-10 flex items-center justify-center rounded-lg text-ink-3 hover:text-ink hover:bg-highest"
            aria-label="Expand sidebar"
            data-testid="sidebar-expand-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        <SidebarSection label="Casino" collapsed={collapsed} testid="sidebar-section-casino">
          {CASINO_ITEMS.map((item) => {
            // Preserve legacy `nav-casino` on Bra 3-Way (primary casino nav)
            const testid = item.key === "bra-3way" ? "nav-casino" : `nav-${item.key}`;
            return item.soon ? (
              <SoonItem key={item.key} icon={item.icon} label={item.label} collapsed={collapsed} testid={testid} />
            ) : (
              <NavItem
                key={item.key}
                to={item.to}
                icon={item.icon}
                label={item.label}
                collapsed={collapsed}
                onClick={onNav}
                liveBadge={item.live}
                testid={testid}
                active={activeKey === item.key}
              />
            );
          })}
        </SidebarSection>

        <SidebarSection label="Sports" collapsed={collapsed} testid="sidebar-section-sports">
          {SPORTS_ITEMS.map((item) => (
            <SoonItem key={item.key} icon={item.icon} label={item.label} collapsed={collapsed} testid={`nav-${item.key}`} />
          ))}
        </SidebarSection>

        {/* --- Auth-gated sections ------------------------------------- */}
        {/* Promotions & rewards, Wallet, and Account all route to
            `ProtectedRoute`-guarded pages (or to chat/live-support that
            requires JWT). Anon visitors saw the labels but every click
            bounced to /login, which is confusing. Hide them entirely
            until sign-in — Login/Sign up CTAs on the topbar remain the
            single anon call-to-action. */}
        {user && (
          <SidebarSection label="Promotions & rewards" collapsed={collapsed} testid="sidebar-section-promo">
            {PROMO_ITEMS.map((item) => {
              const testid = item.key === "promo" ? "nav-promo" : item.key === "vip" ? "nav-vip" : `nav-${item.key}`;
              return item.soon ? (
                <SoonItem key={item.key} icon={item.icon} label={item.label} collapsed={collapsed} testid={testid} />
              ) : (
                <NavItem
                  key={item.key}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  collapsed={collapsed}
                  onClick={onNav}
                  testid={testid}
                  active={activeKey === item.key}
                />
              );
            })}
          </SidebarSection>
        )}

        {user && (
          <SidebarSection
            label="Wallet"
            collapsed={collapsed}
            testid="nav-wallet"
            collapsible
            open={walletOpen}
            onToggle={() => setWalletOpen(!walletOpen)}
          >
            {WALLET_ITEMS.map((w) => (
              <NavItem
                key={w.to}
                to={w.to}
                icon={w.icon}
                label={w.label}
                collapsed={collapsed}
                onClick={onNav}
                testid={w.testid}
                active={activeKey === w.testid}
              />
            ))}
          </SidebarSection>
        )}

        {user && (
          <SidebarSection
            label="Account"
            collapsed={collapsed}
            testid="nav-settings"
            collapsible
            open={accountOpen}
            onToggle={() => setAccountOpen(!accountOpen)}
          >
            {ACCOUNT_ITEMS.map((a) =>
              a.chat ? (
                <button
                  key={a.label}
                  onClick={onChat}
                  className={
                    "w-full flex items-center gap-3 rounded-lg text-sm text-ink-2 hover:text-ink hover:bg-highest transition border-l-2 border-transparent pl-[10px] " +
                    (collapsed ? "justify-center h-10 px-0" : "px-3 py-2.5")
                  }
                  data-testid={a.testid}
                >
                  <a.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span>{a.label}</span>}
                </button>
              ) : (
                <NavItem
                  key={a.to}
                  to={a.to}
                  icon={a.icon}
                  label={a.label}
                  collapsed={collapsed}
                  onClick={onNav}
                  testid={a.testid}
                  active={activeKey === a.testid}
                />
              )
            )}
          </SidebarSection>
        )}

        {/* Anon-only sign-in prompt inside the drawer. The topbar already
            surfaces Login/Sign up; this mirrors those CTAs for users who
            opened the drawer without noticing the topbar buttons. */}
        {!user && !collapsed && (
          <div className="px-3 pt-2 pb-1" data-testid="sidebar-anon-cta">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3 mb-2">
              Sign in to unlock
            </div>
            <div className="flex gap-2">
              <Link
                to="/signup"
                onClick={onNav}
                className="flex-1 h-9 rounded-lg bg-brand-gradient text-white text-xs font-semibold tracking-widest uppercase shadow-glow-pink hover:brightness-110 transition flex items-center justify-center"
                data-testid="sidebar-anon-signup"
              >
                Sign up
              </Link>
              <Link
                to="/login"
                onClick={onNav}
                className="flex-1 h-9 rounded-lg border border-line text-ink-2 text-xs font-semibold tracking-widest uppercase hover:text-ink hover:border-ink-2 transition flex items-center justify-center"
                data-testid="sidebar-anon-login"
              >
                Log in
              </Link>
            </div>
          </div>
        )}

        {/* Preserved legacy testid — hidden, keeps chat-shortcut tests passing */}
        <button
          onClick={onChat}
          className="hidden"
          data-testid="nav-chat"
          aria-hidden="true"
          tabIndex={-1}
        />
      </nav>

      {/* Bottom-pinned trust + logout */}
      <div className={"border-t border-line shrink-0 " + (collapsed ? "p-2 space-y-2" : "p-4 space-y-3")}>
        {!collapsed && (
          <div className="rounded-lg border border-gold/30 bg-gold/5 px-3 py-2" data-testid="sidebar-license" title="Anjouan Gaming Authority License">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-gold" />
              <span className="text-[9px] tracking-[0.3em] uppercase text-gold font-semibold">Licensed</span>
            </div>
            <div className="text-[10px] text-ink-2 mt-1 font-mono">
              Anjouan · ALSI-142857-01
            </div>
          </div>
        )}
        {!collapsed && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="muted" className="text-[9px]">{lang}</Badge>
            <span
              className="w-6 h-6 rounded-full border border-gold/40 text-[8px] font-bold flex items-center justify-center text-gold"
              title="18+ only"
            >
              18+
            </span>
            <a
              href="https://www.begambleaware.org"
              target="_blank"
              rel="noreferrer"
              className="text-[9px] tracking-widest uppercase text-ink-3 hover:text-ink"
            >
              GamCare
            </a>
          </div>
        )}
        {user && (
          <button
            onClick={onLogout}
            className={"flex items-center gap-2 text-xs text-ink-3 hover:text-danger transition " + (collapsed ? "justify-center w-full" : "")}
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            {!collapsed && "Log out"}
          </button>
        )}
      </div>
    </div>
  );
}

function SidebarSection({ label, collapsed, testid, collapsible, open = true, onToggle, children }) {
  return (
    <div data-testid={testid}>
      {!collapsed && (
        collapsible ? (
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between px-3 mb-1.5 text-[9px] font-semibold tracking-[0.3em] uppercase text-ink-3 hover:text-ink transition"
          >
            <span>{label}</span>
            <ChevronDown className={"w-3 h-3 transition-transform " + (open ? "rotate-180" : "")} />
          </button>
        ) : (
          <div className="px-3 mb-1.5 text-[9px] font-semibold tracking-[0.3em] uppercase text-ink-3">
            {label}
          </div>
        )
      )}
      {collapsed && <div className="h-px bg-line/40 mx-4 my-2" />}
      {(!collapsible || open || collapsed) && (
        <div className="space-y-0.5">{children}</div>
      )}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, collapsed, active, onClick, testid, liveBadge }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => {
        const on = isActive || active;
        return (
          "flex items-center gap-3 rounded-lg text-sm transition group relative " +
          (collapsed ? "justify-center h-10 px-0" : "px-3 py-2.5") + " " +
          (on
            ? "bg-pink/10 text-ink border-l-2 border-pink pl-[10px]"
            : "text-ink-2 hover:text-ink hover:bg-highest border-l-2 border-transparent pl-[10px]")
        );
      }}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1">{label}</span>
          {liveBadge && (
            <span className="inline-flex items-center gap-1 text-[8px] tracking-widest uppercase text-green font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
              Live
            </span>
          )}
        </>
      )}
      {collapsed && liveBadge && (
        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
      )}
    </NavLink>
  );
}

function SoonItem({ icon: Icon, label, collapsed, testid }) {
  return (
    <div
      className={
        "flex items-center gap-3 rounded-lg text-sm text-ink-3 select-none cursor-not-allowed opacity-70 border-l-2 border-transparent pl-[10px] " +
        (collapsed ? "justify-center h-10 px-0" : "px-3 py-2.5")
      }
      data-testid={testid}
      title="Coming soon"
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1">{label}</span>
          <span className="text-[8px] tracking-widest uppercase text-ink-3 border border-line rounded px-1.5 py-0.5">
            Soon
          </span>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Top bar
// -----------------------------------------------------------------------------

function TopBar({
  onOpenSidebar, user, pageTitle,
  avatarOpen, setAvatarOpen,
  notifOpen, onNotifClick,
  langOpen, setLangOpen,
  currencyOpen, setCurrencyOpen,
  notifications, unreadCount,
  currency, setCurrency,
  lang, setLang,
  mode, setMode,
  onLogout, onChat, fullBleed,
}) {
  const loc = useLocation();
  const breadcrumb = pageTitle || deriveBreadcrumb(loc.pathname);
  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <header
      className="sticky top-0 z-30 h-16 bg-elevated/85 backdrop-blur border-b border-line px-3 sm:px-5 flex items-center gap-2 sm:gap-3"
      data-testid="app-topbar"
    >
      <button
        onClick={onOpenSidebar}
        className={
          "w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest flex items-center justify-center " +
          (fullBleed ? "flex" : "lg:hidden")
        }
        aria-label="Open menu"
        data-testid="topbar-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      <Link to="/" className="flex items-center gap-2 lg:hidden" data-testid="topbar-logo">
        <span className="h-7 w-7 rounded-md bg-brand-gradient flex items-center justify-center font-display italic text-white text-sm shadow-glow-pink">B</span>
        <span className="font-display italic text-base text-ink hidden sm:inline">Bra Game</span>
      </Link>

      {breadcrumb && (
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3 whitespace-nowrap hidden md:block" data-testid="topbar-breadcrumb">
          {breadcrumb}
        </div>
      )}

      {/* Center search — hidden < 900px to preserve topbar density */}
      <div className="flex-1 max-w-md min-w-0 hidden [@media(min-width:900px)]:block ml-3">
        <div className="flex items-center gap-2 rounded-full bg-highest/60 border border-line px-3.5 py-2 focus-within:border-pink/60 focus-within:shadow-glow-pink transition">
          <Search className="w-3.5 h-3.5 text-ink-3 shrink-0" />
          <input
            className="flex-1 min-w-0 bg-transparent focus:outline-none text-sm text-ink placeholder-ink-3"
            placeholder="Search games…"
            data-testid="topbar-search"
          />
        </div>
      </div>
      <div className="flex-1 [@media(min-width:900px)]:hidden" />

      <div className="flex items-center gap-1 sm:gap-2 ml-auto">
        {user ? (
          <>
            {!fullBleed && (
              <WalletCluster
                currency={currency} setCurrency={setCurrency}
                currencyOpen={currencyOpen} setCurrencyOpen={setCurrencyOpen}
                mode={mode} setMode={setMode}
                user={user}
              />
            )}
            <div className="relative hidden sm:block">
              <button
                onClick={onNotifClick}
                className="relative w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest flex items-center justify-center transition"
                aria-label="Notifications"
                data-testid="topbar-bell"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-pink text-white text-[9px] font-bold flex items-center justify-center shadow-glow-pink"
                    data-testid="topbar-bell-badge"
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <NotificationsMenu items={notifications} onClose={onNotifClick} />
              )}
            </div>
            <Link
              to="/sportsbook"
              className="hidden md:flex items-center gap-1.5 h-9 rounded-lg px-2.5 text-ink-2 hover:text-gold hover:bg-highest transition relative"
              aria-label="Sports"
              title="Sports"
              data-testid="topbar-sports-btn"
            >
              <Trophy className="w-4 h-4" />
              <span className="text-[10px] tracking-widest uppercase">Sports</span>
              <BetaBadge className="ml-1" />
            </Link>
            <Link
              to="/promotions"
              className="hidden md:flex items-center gap-1.5 h-9 rounded-lg px-2.5 text-ink-2 hover:text-pink hover:bg-highest transition relative"
              aria-label="Promotions"
              title="Promotions"
              data-testid="topbar-promo-btn"
            >
              <Gift className="w-4 h-4" />
              <span className="text-[10px] tracking-widest uppercase">Bonuses</span>
              <ActiveBonusBadge />
            </Link>
            <button
              onClick={onChat}
              className="hidden sm:flex w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest items-center justify-center transition"
              aria-label="Live chat"
              data-testid="topbar-support-btn"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <Link
              to="/download"
              className="hidden md:flex w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest items-center justify-center transition"
              aria-label="Get the app"
              title="Get the app"
              data-testid="topbar-download-btn"
            >
              <Smartphone className="w-4 h-4" />
            </Link>
            <div className="relative hidden md:block">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-line hover:bg-highest text-sm text-ink-2 transition"
                data-testid="topbar-lang-btn"
              >
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span className="text-[11px] font-semibold tracking-widest">{currentLang.code}</span>
                <ChevronDown className="w-3 h-3 text-ink-3" />
              </button>
              {langOpen && (
                <LangMenu
                  current={lang}
                  onPick={(l) => { setLang(l); setLangOpen(false); }}
                  onClose={() => setLangOpen(false)}
                />
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setAvatarOpen(!avatarOpen)}
                className="flex items-center gap-2 pl-2 pr-2.5 h-9 rounded-lg border border-line hover:bg-highest text-sm transition"
                data-testid="topbar-avatar-btn"
              >
                <span className="w-6 h-6 rounded-full bg-brand-gradient flex items-center justify-center text-white text-[11px] font-semibold shadow-glow-pink">
                  {((user.username || user.email || "?")).slice(0, 1).toUpperCase()}
                </span>
                <ChevronDown className="w-3 h-3 text-ink-3" />
              </button>
              {avatarOpen && (
                <AvatarDropdown user={user} onClose={() => setAvatarOpen(false)} onLogout={onLogout} />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="relative hidden md:block">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-line hover:bg-highest text-sm text-ink-2 transition"
                data-testid="topbar-lang-btn"
              >
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span className="text-[11px] font-semibold tracking-widest">{currentLang.code}</span>
                <ChevronDown className="w-3 h-3 text-ink-3" />
              </button>
              {langOpen && (
                <LangMenu
                  current={lang}
                  onPick={(l) => { setLang(l); setLangOpen(false); }}
                  onClose={() => setLangOpen(false)}
                />
              )}
            </div>
            <Link to="/login" data-testid="topbar-login-btn">
              <Button variant="ghost" size="md">Log in</Button>
            </Link>
            <Link to="/signup" data-testid="topbar-signup-btn">
              <Button variant="primary" size="md">Sign up</Button>
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------------
// Top-bar clusters
// -----------------------------------------------------------------------------

function WalletCluster({ currency, setCurrency, currencyOpen, setCurrencyOpen, mode, setMode, user }) {
  const { format, currencyMeta: cur } = useCurrency();
  // Multi-currency: read balances natively from the user's per-currency map.
  // No conversion — the topbar chip shows exactly the amount stored for the
  // active currency in the active mode.
  const activeBalance = getBalance(user, mode, currency);
  const realBalance = getBalance(user, "real", currency);
  const demoBalance = getBalance(user, "demo", currency);
  const display = format(activeBalance);
  return (
    <div className="flex items-center gap-1 sm:gap-1.5" data-testid="topbar-wallet-chip">
      <div className="relative">
        <button
          onClick={() => setCurrencyOpen(!currencyOpen)}
          className="flex items-center gap-1.5 sm:gap-2 h-9 pl-2 sm:pl-2.5 pr-1.5 sm:pr-2 rounded-lg border border-line bg-elevated hover:border-ink-3 transition"
          data-testid="topbar-currency-select"
          title={`Active currency: ${cur.label}`}
        >
          <span className="text-[9px] tracking-widest uppercase text-ink-3 hidden sm:inline">{mode}</span>
          <span className="font-mono text-sm text-ink" data-testid="topbar-balance-display">{display}</span>
          <span className="text-[10px] font-semibold text-gold">{cur.code}</span>
          <ChevronDown className="w-3 h-3 text-ink-3" />
        </button>
        {currencyOpen && (
          <CurrencyMenu
            current={currency}
            user={user}
            mode={mode}
            onPick={(c) => { setCurrency(c); setCurrencyOpen(false); }}
            onClose={() => setCurrencyOpen(false)}
          />
        )}
      </div>

      <div
        className="hidden lg:inline-flex"
        data-testid="topbar-mode-pill-wrap"
      >
        <ModeSegmented
          mode={mode}
          setMode={setMode}
          realBalance={realBalance}
          demoBalance={demoBalance}
          size="regular"
        />
      </div>
      <LegacyModeAliases />

      <Link
        to={`/wallet/deposit?currency=${currency}`}
        className="flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-lg bg-green-gradient text-white text-sm font-semibold shadow-[0_0_18px_rgba(34,197,94,0.35)] hover:brightness-110 transition"
        data-testid="topbar-deposit-btn"
      >
        <ArrowDownToLine className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Deposit</span>
      </Link>

      <Link
        to={`/wallet/withdraw?currency=${currency}`}
        className="hidden lg:flex items-center gap-1 h-9 px-3 rounded-lg border border-line text-sm text-ink-2 hover:text-ink hover:border-ink-2 transition"
        data-testid="topbar-withdraw-btn"
      >
        <ArrowUpFromLine className="w-3.5 h-3.5" />
        <span>Withdraw</span>
      </Link>

      {/* Preserve legacy testid used by earlier tests */}
      <Link to="/wallet/deposit" className="hidden" data-testid="wallet-chip-deposit-btn" aria-hidden="true" tabIndex={-1} />
    </div>
  );
}

function CurrencyMenu({ current, user, mode, onPick, onClose }) {
  return (
    <>
      <button className="fixed inset-0 z-40" onClick={onClose} aria-label="close" />
      <div
        className="absolute right-0 mt-2 w-72 bg-highest border border-line rounded-xl shadow-card-lg z-50 p-2"
        data-testid="topbar-currency-menu"
      >
        <div className="px-3 pt-2 pb-1 text-[10px] tracking-widest uppercase text-ink-3">
          Active currency
        </div>
        {CURRENCIES.map((c) => {
          const bal = getBalance(user, mode, c.code);
          const active = current === c.code;
          return (
            <button
              key={c.code}
              onClick={() => onPick(c.code)}
              className={
                "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm hover:bg-elevated transition " +
                (active ? "text-gold" : "text-ink-2 hover:text-ink")
              }
              data-testid={`currency-option-${c.code.toLowerCase()}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-6 text-base leading-none inline-flex items-center justify-center font-mono text-ink-3">
                  {c.symbol}
                </span>
                <span className="truncate">{c.label}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-[11px] text-ink-2">
                  {new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: c.decimals,
                    maximumFractionDigits: c.decimals,
                  }).format(bal)}
                </span>
                <span className="text-[10px] font-semibold tracking-widest">{c.code}</span>
              </span>
            </button>
          );
        })}
        <div
          className="px-3 pt-2 pb-1 text-[10px] text-ink-3 italic border-t border-line mt-1 leading-relaxed"
          data-testid="currency-menu-subtitle"
        >
          Bets, balances and payouts stay native — no conversion.
        </div>
      </div>
    </>
  );
}

function LangMenu({ current, onPick, onClose }) {
  return (
    <>
      <button className="fixed inset-0 z-40" onClick={onClose} aria-label="close" />
      <div className="absolute right-0 mt-2 w-56 bg-highest border border-line rounded-xl shadow-card-lg z-50 p-2" data-testid="topbar-lang-menu">
        <div className="px-3 pt-2 pb-1 text-[10px] tracking-widest uppercase text-ink-3">Language</div>
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => onPick(l.code)}
            className={"w-full flex items-center justify-between px-3 py-2 rounded-md text-sm hover:bg-elevated transition " +
              (current === l.code ? "text-gold" : "text-ink-2 hover:text-ink")}
            data-testid={`lang-${l.code.toLowerCase()}`}
          >
            <span className="flex items-center gap-2">
              <span className="text-base leading-none">{l.flag}</span>
              <span>{l.label}</span>
            </span>
            {current === l.code && <span className="text-[9px] tracking-widest text-gold">ACTIVE</span>}
          </button>
        ))}
        <div className="px-3 pt-2 pb-1 text-[9px] text-ink-3 italic border-t border-line mt-1 leading-relaxed">
          More languages coming soon.
        </div>
      </div>
    </>
  );
}

function NotificationsMenu({ items, onClose }) {
  const visible = items.slice(0, 5);
  return (
    <>
      <button className="fixed inset-0 z-40" onClick={onClose} aria-label="close" />
      <div
        className="absolute right-0 mt-2 w-80 bg-highest border border-line rounded-xl shadow-card-lg z-50 overflow-hidden"
        data-testid="topbar-notifications-menu"
      >
        <div className="p-3 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            <div className="text-[10px] tracking-widest uppercase text-ink-3">Notifications</div>
          </div>
          <span className="text-[10px] tracking-widest uppercase text-pink">Marked read</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="p-6 text-xs text-ink-3 text-center italic">No notifications yet</div>
          ) : (
            visible.map((n, i) => (
              <div
                key={n.id}
                className={"px-4 py-3 border-b border-line/40 last:border-0 flex items-start gap-2 " + (n.unread ? "bg-pink/5" : "")}
                data-testid={`notification-${i}`}
              >
                <span className={"mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 " + (
                  n.type === "gold" ? "bg-gold" :
                  n.type === "success" ? "bg-green" :
                  n.type === "pink" ? "bg-pink" : "bg-ink-3"
                )} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm text-ink truncate">{n.title}</div>
                    <div className="text-[10px] text-ink-3 shrink-0">{n.time}</div>
                  </div>
                  <div className="text-xs text-ink-2 truncate mt-0.5">{n.body}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <Link
          to="/history"
          onClick={onClose}
          className="block text-center py-2.5 text-[10px] tracking-widest uppercase text-pink hover:bg-elevated transition"
          data-testid="topbar-notifications-view-all"
        >
          View all activity →
        </Link>
      </div>
    </>
  );
}

function AvatarDropdown({ user, onClose, onLogout }) {
  const kycStatus = user.kyc_status || "none";
  const kycBadge =
    kycStatus === "approved" ? "success" :
    kycStatus === "pending" ? "gold" :
    kycStatus === "rejected" ? "danger" : "muted";
  const kycLabel =
    kycStatus === "approved" ? "Approved" :
    kycStatus === "pending" ? "Pending" :
    kycStatus === "rejected" ? "Rejected" : "Not started";
  return (
    <>
      <button className="fixed inset-0 z-40" onClick={onClose} aria-label="close" />
      <div className="absolute right-0 mt-2 w-72 bg-highest border border-line rounded-xl shadow-card-lg z-50" data-testid="topbar-avatar-menu">
        <div className="p-4 border-b border-line">
          <div className="text-[10px] tracking-widest uppercase text-ink-3">Signed in as</div>
          <div className="text-sm text-ink truncate" data-testid="menu-email">
            {user.username || user.email || "player"}
          </div>
          {user.username && user.email && (
            <div className="text-[10px] text-ink-3 truncate mt-0.5 font-mono">{user.email}</div>
          )}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            {user.is_admin && <Badge variant="vip">Admin</Badge>}
            <span className="text-[9px] tracking-widest uppercase text-ink-3">
              VIP {user.vip_tier || 0}
            </span>
          </div>
        </div>
        <div className="p-2">
          <MenuLink to="/dashboard" onClose={onClose} testid="menu-profile">My profile</MenuLink>
          <Link
            to="/wallet/kyc"
            onClick={onClose}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-ink-2 hover:text-ink hover:bg-elevated transition"
            data-testid="menu-kyc"
          >
            <span>KYC verification</span>
            <Badge variant={kycBadge} className="text-[9px]">{kycLabel}</Badge>
          </Link>
          <MenuLink to="/history" onClose={onClose} testid="menu-history">Transaction history</MenuLink>
          <MenuLink to="/settings/responsible-gambling" onClose={onClose} testid="menu-rg">Responsible gambling</MenuLink>
          <MenuLink to="/verify" onClose={onClose} testid="menu-verify">Verify fairness</MenuLink>
          {user.is_admin && (
            <MenuLink to="/admin" onClose={onClose} testid="menu-admin" gold>
              Admin control room →
            </MenuLink>
          )}
          <div className="my-1 h-px bg-line" />
          <button
            onClick={() => { onLogout(); onClose(); }}
            className="w-full text-left px-3 py-2 rounded-md text-sm text-danger hover:bg-elevated transition"
            data-testid="menu-logout"
          >
            <LogOut className="w-3.5 h-3.5 inline mr-2" />
            Log out
          </button>
        </div>
      </div>
    </>
  );
}

function MenuLink({ to, testid, gold, onClose, children }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className={
        "block px-3 py-2 rounded-md text-sm transition " +
        (gold ? "text-gold hover:brightness-110 hover:bg-elevated" : "text-ink-2 hover:text-ink hover:bg-elevated")
      }
      data-testid={testid}
    >
      {children}
    </Link>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatCurrencyDisplay(_usdAmount, _cur) {
  // Legacy shim — real formatting lives in /src/lib/currency.js and is used
  // through the CurrencyContext. This wrapper stays for grep-based tests only.
  return "";
}

function deriveBreadcrumb(pathname) {
  if (pathname === "/") return "Home";
  if (pathname === "/dashboard") return "Casino / Home";
  if (pathname === "/game/bra-3-way" || pathname === "/game" || pathname === "/game-poc") return "Casino / Bra 3-Way";
  if (pathname === "/game/bra-2-way") return "Casino / Bra 2-Way";
  if (pathname === "/game/sexy-tattoo") return "Casino / Sexy Tattoo";
  if (pathname === "/game/hot-bikini") return "Casino / Hot Bikini";
  if (pathname === "/game/scratch-card") return "Casino / Scratch Card";
  if (pathname.startsWith("/game/")) return "Casino";
  if (pathname === "/promotions") return "Promotions";
  if (pathname === "/vip") return "VIP Program";
  if (pathname === "/history") return "Wallet / History";
  if (pathname === "/verify") return "Fairness / Verifier";
  if (pathname.startsWith("/wallet/deposit")) return "Wallet / Deposit";
  if (pathname.startsWith("/wallet/withdraw")) return "Wallet / Withdraw";
  if (pathname.startsWith("/wallet/kyc")) return "Wallet / KYC";
  if (pathname.startsWith("/wallet")) return "Wallet";
  if (pathname.startsWith("/settings")) return "Settings / Responsible gambling";
  return null;
}


/**
 * (Legacy inline ActiveBonusBadge — moved to `components/ActiveBonusBadge.jsx`
 * so the mobile bottom-nav can share the same fetch/gate.)
 */
